import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, BACKEND_URL } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { STYLES, MOODS, BUDGETS, KEYS } from "../lib/constants";
import { StyleTag, SectionTitle, Card } from "../components/ui-bits";
import { Loader2, Check, Copy, Share2, X } from "lucide-react";
import { toast } from "sonner";

export default function CreateSearch() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [form, setForm] = useState({
    title: "", styles: [], moods: [], bpm_min: "", bpm_max: "", key: "",
    references: "", description: "", budget: "À discuter",
  });
  const [busy, setBusy] = useState(false);
  const [published, setPublished] = useState(null);

  const toggle = (field, val) => {
    const cur = form[field];
    setForm({ ...form, [field]: cur.includes(val) ? cur.filter((x) => x !== val) : [...cur, val] });
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return toast.error("Ajoute un titre à ta recherche");
    if (form.styles.length === 0) return toast.error("Sélectionne au moins un style");
    setBusy(true);
    try {
      const payload = {
        ...form,
        bpm_min: form.bpm_min ? parseInt(form.bpm_min) : null,
        bpm_max: form.bpm_max ? parseInt(form.bpm_max) : null,
      };
      if (!user) {
        localStorage.setItem("ilm_pending_search", JSON.stringify(payload));
        toast.success("Crée ton compte pour publier ta recherche");
        nav("/register?intent=artist");
        return;
      }
      const { data } = await api.post("/searches", payload);
      setPublished(data);
    } catch (e2) {
      toast.error(formatApiError(e2.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const shareUrl = published ? `${window.location.origin}/r/${published.code}` : "";

  const copyLink = () => { navigator.clipboard.writeText(shareUrl); toast.success("Lien copié !"); };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: published.title, text: "Découvre ma recherche de prod sur ilovemuzik", url: shareUrl }); } catch {}
    } else { copyLink(); }
  };

  if (published) {
    const encoded = encodeURIComponent(`Je cherche une prod 🎧 ${shareUrl}`);
    const socials = [
      { name: "WhatsApp", href: `https://wa.me/?text=${encoded}`, color: "bg-[#25D366]", testid: "share-whatsapp-button" },
      { name: "Telegram", href: `https://t.me/share/url?url=${encodeURIComponent(shareUrl)}`, color: "bg-[#0088cc]", testid: "share-telegram-button" },
      { name: "X / Twitter", href: `https://twitter.com/intent/tweet?text=${encoded}`, color: "bg-zinc-700", testid: "share-x-button" },
    ];
    return (
      <div className="max-w-lg mx-auto animate-fade-up">
        <Card className="p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-4">
            <Check size={30} className="text-emerald-400" />
          </div>
          <h1 className="text-2xl font-display font-bold mb-1">Ta recherche est en ligne !</h1>
          <p className="text-sm text-zinc-400 mb-6">Partage le lien pour recevoir des productions.</p>

          <div className="flex items-center gap-2 bg-zinc-900 border border-zinc-700 rounded-xl p-2 pl-4 mb-4">
            <span className="text-sm font-mono text-violet-300 truncate flex-1 text-left" data-testid="share-link-text">{shareUrl}</span>
            <button onClick={copyLink} data-testid="copy-link-button" className="shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-lg bg-violet-600 hover:bg-violet-500 text-sm font-medium transition-colors">
              <Copy size={15} /> Copier
            </button>
          </div>

          <button onClick={nativeShare} data-testid="native-share-button" className="w-full flex items-center justify-center gap-2 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold mb-3 transition-all">
            <Share2 size={18} /> Partager
          </button>

          <div className="grid grid-cols-3 gap-2">
            {socials.map((s) => (
              <a key={s.name} href={s.href} target="_blank" rel="noreferrer" data-testid={s.testid}
                className={`${s.color} py-2.5 rounded-xl text-sm font-medium text-white hover:opacity-90 transition-opacity`}>
                {s.name}
              </a>
            ))}
          </div>

          <div className="flex gap-2 mt-6">
            <button onClick={() => nav(`/app/search/${published.id}`)} className="flex-1 py-2.5 rounded-xl border border-zinc-700 hover:border-violet-600 text-sm font-medium transition-colors" data-testid="view-my-search-button">
              Voir ma recherche
            </button>
            <button onClick={() => nav("/app")} className="flex-1 py-2.5 rounded-xl bg-zinc-800 hover:bg-zinc-700 text-sm font-medium transition-colors">
              Mon tableau
            </button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <SectionTitle sub="Décris précisément le son que tu recherches.">Nouvelle recherche</SectionTitle>
      <form onSubmit={submit} className="space-y-6">
        <Card className="p-5 space-y-5">
          <div>
            <label className="text-sm font-medium text-zinc-300">Titre</label>
            <input data-testid="search-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Je recherche une prod pour mon prochain single"
              className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300">Styles musicaux</label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="search-styles">
              {STYLES.map((s) => <StyleTag key={s} active={form.styles.includes(s)} onClick={() => toggle("styles", s)}>{s}</StyleTag>)}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="text-sm font-medium text-zinc-300">BPM min</label>
              <input data-testid="search-bpm-min" type="number" value={form.bpm_min} onChange={(e) => setForm({ ...form, bpm_min: e.target.value })}
                className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-3 text-sm font-mono focus:border-violet-500 outline-none" placeholder="90" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">BPM max</label>
              <input data-testid="search-bpm-max" type="number" value={form.bpm_max} onChange={(e) => setForm({ ...form, bpm_max: e.target.value })}
                className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-3 text-sm font-mono focus:border-violet-500 outline-none" placeholder="150" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-300">Tonalité</label>
              <select data-testid="search-key" value={form.key} onChange={(e) => setForm({ ...form, key: e.target.value })}
                className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-3 py-3 text-sm font-mono focus:border-violet-500 outline-none">
                {KEYS.map((k) => <option key={k} value={k}>{k || "—"}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300">Ambiance</label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="search-moods">
              {MOODS.map((m) => <StyleTag key={m} active={form.moods.includes(m)} onClick={() => toggle("moods", m)}>{m}</StyleTag>)}
            </div>
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300">Artistes de référence</label>
            <input data-testid="search-references" value={form.references} onChange={(e) => setForm({ ...form, references: e.target.value })}
              placeholder="Ninho / Tiakola…" className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300">Description</label>
            <textarea data-testid="search-description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={4} placeholder="Décris ton univers, ce que tu recherches…"
              className="mt-1.5 w-full bg-zinc-900 border border-zinc-700 rounded-xl px-4 py-3 text-sm focus:border-violet-500 outline-none resize-none" />
          </div>
          <div>
            <label className="text-sm font-medium text-zinc-300">Budget</label>
            <div className="flex flex-wrap gap-2 mt-2" data-testid="search-budget">
              {BUDGETS.map((b) => <StyleTag key={b} active={form.budget === b} onClick={() => setForm({ ...form, budget: b })}>{b}</StyleTag>)}
            </div>
          </div>
        </Card>
        <button data-testid="publish-search-button" disabled={busy} type="submit"
          className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-500 hover:to-purple-500 font-semibold text-white glow-violet transition-all disabled:opacity-60">
          {busy && <Loader2 size={16} className="animate-spin" />} PUBLIER MA RECHERCHE
        </button>
      </form>
    </div>
  );
}
